import {withAuthenticationRequired} from '@auth0/auth0-react';
import {Field, Form, Formik} from 'formik';
import {FC} from 'react';
import toast from 'react-hot-toast';
import {useNavigate} from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import {useCreateUser} from '../../API';
import {
  BoldFieldLabel,
  Dropdown,
  ErrorBody,
  FieldWrapper,
  FlexRow,
  FormField as FormFieldBase,
  H4,
  RequiredSpan,
  StandardButton,
  TextInput,
} from '../../Components';
import {routePaths} from '../../constants/routePaths';
import {requireAdmin} from '../../Managers/AuthUtils';
import {Header} from '../Map/Header';
import {AdminMenu} from './AdminMenu';
import {AdminPage, AdminPageWrapper, AdminPanel, AdminRow} from './Layout';
const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  user_type: Yup.string().required('User type is required'),
  user_status: Yup.string().required('User status is required'),
  firstName: Yup.string(),
  lastName: Yup.string(),
});

const userTypeOptions = [
  {label: 'Greenlink Staff', value: 'admin'},
  {label: 'Organization Admin', value: 'org-admin'},
  {label: 'User', value: 'user'},
];

const userStatusOptions = [
  {label: 'Active', value: 'active'},
  {label: 'Inactive', value: 'inactive'},
];

const FormField = styled(FormFieldBase)`
  flex: 1;
`;

const CreateUserComponent: FC = () => {
  const createUser = useCreateUser();
  const navigate = useNavigate();

  const handleCreateUser = async (values: {
    email: string;
    user_type: string;
    user_status: string;
    firstName?: string;
    lastName?: string;
  }) => {
    await createUser
      .mutateAsync(values)
      .then((r) => {
        toast.success('User has been successfully created!');
        navigate(routePaths.adminUsers);
      })
      .catch((e) => {
        console.log('save error', e);
      });
  };

  return (
    <AdminPageWrapper>
      <Header />
      <AdminRow>
        <AdminMenu />

        <AdminPage>
          <H4>Create User</H4>

          <AdminPanel>
            <Formik
              initialValues={{
                email: '',
                user_type: 'user',
                user_status: 'active',
                firstName: '',
                lastName: '',
              }}
              validationSchema={validationSchema}
              onSubmit={handleCreateUser}>
              {({errors, touched, isSubmitting}) => (
                <Form>
                  <FormField>
                    <TextInput name="email" label="Email" placeholder="Enter email address" required />
                    {/* {errors.email && touched.email && <ErrorBody>{errors.email}</ErrorBody>} */}
                  </FormField>

                  <FlexRow style={{gap: '2rem'}}>
                    <FormField>
                      <Field name="user_type">
                        {({field, form}: any) => (
                          <FieldWrapper>
                            <BoldFieldLabel>
                              User Type
                              <RequiredSpan> *</RequiredSpan>
                            </BoldFieldLabel>

                            <Dropdown
                              options={userTypeOptions}
                              value={userTypeOptions.find((option) => option.value === field.value)}
                              onChange={(option: any) => form.setFieldValue('user_type', option.value)}
                            />
                          </FieldWrapper>
                        )}
                      </Field>

                      {errors.user_type && touched.user_type && <ErrorBody>{errors.user_type}</ErrorBody>}
                    </FormField>

                    <FormField>
                      <Field name="user_status">
                        {({field, form}: any) => (
                          <FieldWrapper>
                            <BoldFieldLabel>
                              User Status
                              <RequiredSpan> *</RequiredSpan>
                            </BoldFieldLabel>

                            <Dropdown
                              options={userStatusOptions}
                              value={userStatusOptions.find((option) => option.value === field.value)}
                              onChange={(option: any) => form.setFieldValue('user_status', option.value)}
                            />
                          </FieldWrapper>
                        )}
                      </Field>
                      {errors.user_status && touched.user_status && <ErrorBody>{errors.user_status}</ErrorBody>}
                    </FormField>
                  </FlexRow>

                  <FlexRow style={{gap: '2rem', flex: 1, width: '100%'}}>
                    <FormField>
                      <TextInput name="firstName" label="First Name" placeholder="Enter first name" />
                    </FormField>

                    <FormField>
                      <TextInput name="lastName" label="Last Name" placeholder="Enter last name" />
                    </FormField>
                  </FlexRow>

                  <StandardButton type="submit" disabled={isSubmitting} style={{marginTop: '1rem', width: '100%'}}>
                    Create User
                  </StandardButton>
                </Form>
              )}
            </Formik>
          </AdminPanel>
        </AdminPage>
      </AdminRow>
    </AdminPageWrapper>
  );
};

export const CreateUser: FC = withAuthenticationRequired(CreateUserComponent, {claimCheck: requireAdmin});
